import React, { useEffect, useMemo, useState } from 'react';
import { Button, Card, Space, Table, Typography } from 'antd';
import { VegaLite } from 'react-vega';
import {
  BarChartOutlined,
  LineChartOutlined,
  PieChartOutlined,
  TableOutlined,
} from '@ant-design/icons';

import { formatColumnLabel, isInternalIdColumn, renderSummary, splitFollowUps } from './markdownRender';
import {
  getDetailedViewStatusLabel,
  statusLabel,
} from '../../pages/MH_Admin/MH_Reporting_Dashboard/detailedViewHelpers';
import styles from './codiAnalyticsResult.module.css';

const { Text } = Typography;

const CHART_COLOR = '#315e45';
const PIE_SCHEME = 'tableau10';

const FRONTEND_STATUS_LABELS = {
  ...statusLabel,
  IN_PROGRESS: 'In progress',
  UNABLE_TO_FULFILL: 'Unable to fulfill',
  DECLINED: 'Declined',
  NEW_REQUEST: 'New request',
  BILLED: 'Billed',
  CLAIMED: 'Claimed',
};

const RIDE_INDEX_STATUS_COLORS = {
  PENDING: '#828282',
  NEW_REQUEST: '#828282',
  UNABLE_TO_FULFILL: '#828282',
  CLAIMED: '#828282',
  BILLED: '#828282',
  COMPLETED: '#219653',
  CANCELED: '#eb5757',
  EXPIRED: '#eb5757',
  DECLINED: '#eb5757',
  UNFULFILLED: '#eb5757',
  ACCEPTED: '#2f80ed',
  IN_PROGRESS: '#2f80ed',
  START: '#2f80ed',
  STARTED: '#2f80ed',
};

const RIDE_INDEX_STATUS_FALLBACK = '#303030';

const STATUS_TOKEN_RE = new RegExp(
  `\\b(?:${Object.keys(FRONTEND_STATUS_LABELS)
    .sort((a, b) => b.length - a.length)
    .map((key) => key.replace(/_/g, '[\\s_]+'))
    .join('|')})\\b`,
  'gi',
);

const isStatusColumn = (column) => {
  const name = String(column || '')
    .toLowerCase()
    .replace(/[\s-]/g, '_');
  return name === 'status' || name.endsWith('_status') || name.endsWith('status');
};

const hasStatusColumn = (columns = []) => columns.some(isStatusColumn);

const toStatusKey = (value) =>
  String(value ?? '')
    .trim()
    .replace(/\s+/g, '_')
    .toUpperCase();

const frontendStatusLabel = (value) => {
  if (value === null || value === undefined || value === '') return value;
  const key = toStatusKey(value);
  return FRONTEND_STATUS_LABELS[key] || getDetailedViewStatusLabel(key);
};

const hexToRgba = (hex, alpha) => {
  const raw = String(hex || '').replace('#', '');
  const full = raw.length === 3 ? raw.split('').map((c) => `${c}${c}`).join('') : raw;
  const n = parseInt(full, 16);
  if (Number.isNaN(n)) return `rgba(51, 65, 85, ${alpha})`;
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
};

const StatusPill = ({ status }) => {
  if (status === null || status === undefined || status === '') return '—';
  const key = toStatusKey(status);
  const color = RIDE_INDEX_STATUS_COLORS[key] || RIDE_INDEX_STATUS_FALLBACK;
  return (
    <span
      className={styles.statusPill}
      style={{ color, background: hexToRgba(color, 0.14) }}>
      <span className={styles.statusDot} style={{ background: color }} />
      {frontendStatusLabel(status)}
    </span>
  );
};

const applyFrontendStatusLabels = (text) =>
  String(text).replace(STATUS_TOKEN_RE, (match) => frontendStatusLabel(match));

const cellText = (value) => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

const displayCell = (column, value) => {
  if (value === null || value === undefined || value === '') return '—';
  return isStatusColumn(column) ? frontendStatusLabel(value) : cellText(value);
};

const isNumericColumn = (rows, column) => {
  let seen = 0;
  for (const row of rows) {
    const value = row?.[column];
    if (value === null || value === undefined || value === '') continue;
    const parsed = Number(String(value).replace(/,/g, ''));
    if (Number.isNaN(parsed)) return false;
    seen += 1;
  }
  return seen > 0;
};

const looksLikeDate = (value) => {
  const s = cellText(value);
  return /^\d{4}-\d{2}/.test(s) || /^\d{1,2}\/\d{1,2}\/\d{2,4}/.test(s);
};

const pickChartColumns = (columns, rows) => {
  if (rows.length < 2 || rows.length > 200) return null;

  const numericCols = columns.filter((c) => isNumericColumn(rows, c));
  const categoricalCols = columns.filter((c) => !numericCols.includes(c));
  if (!numericCols.length || !categoricalCols.length) return null;

  let labelCol = categoricalCols.find((c) => rows.some((r) => looksLikeDate(r[c])));
  if (!labelCol) {
    labelCol =
      categoricalCols.find((c) => {
        const distinct = new Set(rows.map((r) => cellText(r[c]))).size;
        return distinct > 1 && distinct <= rows.length;
      }) || categoricalCols[0];
  }

  return { labelCol, numericCols, isTemporal: rows.some((r) => looksLikeDate(r[labelCol])) };
};

const ResultTable = ({ columns, rows }) => {
  const [current, setCurrent] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  useEffect(() => {
    setCurrent(1);
  }, [rows]);

  const numericByColumn = useMemo(() => {
    const map = {};
    columns.forEach((c) => {
      map[c] = isNumericColumn(rows, c);
    });
    return map;
  }, [columns, rows]);

  const dataSource = useMemo(
    () => rows.map((row, idx) => ({ ...row, key: idx })),
    [rows],
  );

  const tableColumns = columns.map((col) => ({
    title: formatColumnLabel(col),
    dataIndex: col,
    key: col,
    align: numericByColumn[col] ? 'right' : 'left',
    sorter: (a, b) => {
      if (numericByColumn[col]) {
        return (
          Number(String(a?.[col] ?? '').replace(/,/g, '')) -
          Number(String(b?.[col] ?? '').replace(/,/g, ''))
        );
      }
      return displayCell(col, a?.[col]).localeCompare(displayCell(col, b?.[col]));
    },
    render: (value) =>
      isStatusColumn(col) ? <StatusPill status={value} /> : displayCell(col, value),
  }));

  return (
    <Card
      size="small"
      className={styles.card}
      title={
        <Space>
          <TableOutlined className={styles.cardIcon} />
          <Text strong>Result</Text>
          <Text type="secondary" className={styles.cardMeta}>
            {rows.length} {rows.length === 1 ? 'row' : 'rows'} · {columns.length}{' '}
            {columns.length === 1 ? 'col' : 'cols'}
          </Text>
        </Space>
      }>
      <Table
        dataSource={dataSource}
        columns={tableColumns}
        pagination={
          rows.length > 10
            ? {
                current,
                pageSize,
                total: rows.length,
                showSizeChanger: true,
                pageSizeOptions: [10, 20, 50, 100],
                size: 'small',
                onChange: (page, size) => {
                  setCurrent(page);
                  setPageSize(size);
                },
              }
            : false
        }
        size="small"
        bordered
        scroll={{ x: true }}
      />
    </Card>
  );
};

const ResultChart = ({ columns, rows }) => {
  const picked = useMemo(() => pickChartColumns(columns, rows), [columns, rows]);
  const [chartType, setChartType] = useState(null);
  const [valueCol, setValueCol] = useState(null);

  if (!picked) return null;

  const { labelCol, numericCols, isTemporal } = picked;
  const activeType = chartType || (isTemporal ? 'line' : 'bar');
  const activeValue = valueCol && numericCols.includes(valueCol) ? valueCol : numericCols[0];

  const data = rows.map((row) => ({
    label: isStatusColumn(labelCol) ? frontendStatusLabel(row[labelCol]) : cellText(row[labelCol]),
    value: Number(String(row[activeValue] ?? '').replace(/,/g, '')) || 0,
  }));

  const baseSpec = {
    $schema: 'https://vega.github.io/schema/vega-lite/v5.json',
    data: { values: data },
    width: 'container',
    height: 300,
    autosize: { type: 'fit', contains: 'padding', resize: true },
    config: { view: { stroke: 'transparent' }, axis: { labelFontSize: 11, titleFontSize: 12 } },
  };

  const spec =
    activeType === 'pie'
      ? {
          ...baseSpec,
          mark: { type: 'arc', innerRadius: 60, tooltip: true },
          encoding: {
            theta: { field: 'value', type: 'quantitative' },
            color: {
              field: 'label',
              type: 'nominal',
              title: formatColumnLabel(labelCol),
              scale: { scheme: PIE_SCHEME },
            },
          },
        }
      : {
          ...baseSpec,
          mark:
            activeType === 'line'
              ? { type: 'line', point: true, tooltip: true, color: CHART_COLOR }
              : { type: 'bar', cornerRadiusEnd: 4, tooltip: true, color: CHART_COLOR },
          encoding: {
            x: {
              field: 'label',
              type: isTemporal && activeType === 'line' ? 'temporal' : 'nominal',
              title: formatColumnLabel(labelCol),
              sort: null,
              axis: { labelAngle: data.length > 8 ? -40 : 0 },
            },
            y: {
              field: 'value',
              type: 'quantitative',
              title: formatColumnLabel(activeValue),
            },
          },
        };

  const typeOptions = [
    { key: 'bar', label: 'Bar', icon: <BarChartOutlined /> },
    { key: 'line', label: 'Line', icon: <LineChartOutlined /> },
    { key: 'pie', label: 'Pie', icon: <PieChartOutlined /> },
  ];

  return (
    <Card
      size="small"
      className={styles.card}
      title={
        <Space>
          <BarChartOutlined className={styles.cardIcon} />
          <Text strong>Visualize</Text>
        </Space>
      }
      extra={
        <Space size={4} wrap>
          {numericCols.length > 1 &&
            numericCols.map((col) => (
              <Button
                key={col}
                size="small"
                type={col === activeValue ? 'primary' : 'default'}
                onClick={() => setValueCol(col)}>
                {formatColumnLabel(col)}
              </Button>
            ))}
          {typeOptions.map((opt) => (
            <Button
              key={opt.key}
              size="small"
              icon={opt.icon}
              type={opt.key === activeType ? 'primary' : 'default'}
              onClick={() => setChartType(opt.key)}>
              {opt.label}
            </Button>
          ))}
        </Space>
      }>
      <div className={styles.chartWrap}>
        <VegaLite spec={spec} actions={false} renderer="svg" style={{ width: '100%' }} />
      </div>
    </Card>
  );
};

const FollowUps = ({ items, onSelect, disabled }) => (
  <div className={styles.followUps}>
    {items.map((question) => (
      <button
        key={question}
        type="button"
        title={question}
        className={styles.followUp}
        disabled={disabled}
        onClick={() => onSelect(question)}>
        {question}
      </button>
    ))}
  </div>
);

export const CodiAnalyticsFollowUps = ({ result, onSelect, disabled }) => {
  if (!result?.text) return null;
  const { followUps } = splitFollowUps(result.text);
  if (!followUps.length) return null;
  return <FollowUps items={followUps} onSelect={onSelect} disabled={disabled} />;
};

/**
 * Renders one assistant answer from the Codi AI agent.
 *
 * Expected payload (flattened by the cod-mhadmin service):
 *   { text, sql, columns, rows, thoughts, datasources, job }
 */
export const CodiAnalyticsResult = ({ result, error }) => {
  if (error) {
    return (
      <Card size="small" className={styles.errorCard}>
        <Text type="danger">{error}</Text>
      </Card>
    );
  }

  if (!result) return null;

  const rows = Array.isArray(result.rows) ? result.rows : [];
  const rawColumns =
    Array.isArray(result.columns) && result.columns.length
      ? result.columns
      : rows[0]
        ? Object.keys(rows[0])
        : [];
  const columns = rawColumns.filter((column) => !isInternalIdColumn(column));

  const { body } = splitFollowUps(result.text);
  const summary = hasStatusColumn(columns) ? applyFrontendStatusLabels(body) : body;

  return (
    <div className={styles.result}>
      {summary ? <div className={styles.answer}>{renderSummary(summary)}</div> : null}

      {rows.length > 0 && columns.length > 0 && (
        <>
          <ResultTable columns={columns} rows={rows} />
          <ResultChart columns={columns} rows={rows} />
        </>
      )}
    </div>
  );
};

export default CodiAnalyticsResult;
